/** Jansunwai display zone — all officer portal clocks use Asia/Kolkata. */
export const APP_TZ = 'Asia/Kolkata';
export const APP_TZ_OFFSET = '+05:30';

export const DATE_FMT = 'd MMM y';
export const DATETIME_FMT = 'd MMM y, HH:mm';

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Parts of `date` in Asia/Kolkata. */
export function istParts(date = new Date()): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
} {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const bag = Object.fromEntries(
    fmt
      .formatToParts(date)
      .filter((p) => p.type !== 'literal')
      .map((p) => [p.type, p.value]),
  );
  const hour = bag['hour'] === '24' ? 0 : Number(bag['hour']);
  return {
    year: Number(bag['year']),
    month: Number(bag['month']),
    day: Number(bag['day']),
    hour,
    minute: Number(bag['minute']),
    second: Number(bag['second']),
  };
}

/** YYYY-MM-DD in Asia/Kolkata. */
export function todayDateOnly(date = new Date()): string {
  const p = istParts(date);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/** Short month label in en-IN / IST (e.g. Jul). */
export function monthShort(date = new Date()): string {
  return new Intl.DateTimeFormat('en-IN', { timeZone: APP_TZ, month: 'short' }).format(date);
}

/** Calendar year-month label, e.g. "July 2026". */
export function monthYearLabel(date = new Date()): string {
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: APP_TZ,
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/** Days 1..N for current IST month. */
export function daysInCurrentMonth(date = new Date()): number[] {
  const p = istParts(date);
  const count = new Date(Date.UTC(p.year, p.month, 0)).getUTCDate();
  return Array.from({ length: count }, (_, i) => i + 1);
}

/** Next N calendar days starting today (IST), for roster strip. */
export function upcomingDays(count = 7, date = new Date()): { day: number; monthShort: string; iso: string }[] {
  const out: { day: number; monthShort: string; iso: string }[] = [];
  const base = istParts(date);
  for (let i = 0; i < count; i++) {
    const utc = Date.UTC(base.year, base.month - 1, base.day + i);
    const d = new Date(utc);
    // Interpret UTC Y-M-D as the calendar day we computed
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth() + 1;
    const day = d.getUTCDate();
    const iso = `${y}-${pad(m)}-${pad(day)}`;
    const labelDate = new Date(`${iso}T12:00:00${APP_TZ_OFFSET}`);
    out.push({
      day,
      monthShort: monthShort(labelDate),
      iso,
    });
  }
  return out;
}

/**
 * Normalize API timestamps for DatePipe (Asia/Kolkata).
 * - Already zoned ISO → keep
 * - Naive MySQL DATETIME (UTC storage) → append Z
 * - Date-only → noon IST (stable calendar day under DatePipe)
 * - `YYYY-MM-DDTHH:mm` hearing slot without zone → +05:30
 */
export function toDisplayInstant(value: string | Date | null | undefined): string | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return value.toISOString();
  const s = String(value).trim();
  if (!s) return null;
  // Already a proper zoned ISO instant — not just "ends with an offset" (a malformed
  // upstream string can end that way too and must not be passed through as-is).
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?\s*(Z|[+-]\d{2}:?\d{2})$/.test(s)) {
    return s.includes(' ') ? s.replace(' ', 'T') : s;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return `${s}T12:00:00${APP_TZ_OFFSET}`;
  }
  // Explicit hearing-style local: date + time, no fractional seconds, no space form
  const slot = s.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::(\d{2}))?$/);
  if (slot) {
    return `${slot[1]}T${slot[2]}:${slot[3] || '00'}${APP_TZ_OFFSET}`;
  }
  // MySQL UTC DATETIME: "YYYY-MM-DD HH:mm:ss[.ffffff]"
  const mysql = s.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})(\.\d+)?$/);
  if (mysql) {
    return `${mysql[1]}T${mysql[2]}${mysql[3] || ''}Z`;
  }
  // Last resort: let the JS Date parser take a shot (handles things like full
  // `Date.toString()` output). Never hand DatePipe something unparseable — that
  // throws and aborts the whole change-detection pass, not just this binding.
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Build IST ISO for scheduling from calendar day + HH:mm. */
export function buildIstScheduledAt(year: number, month: number, day: number, hm: string): string {
  const time = hm.length === 5 ? `${hm}:00` : hm;
  return `${year}-${pad(month)}-${pad(day)}T${time}${APP_TZ_OFFSET}`;
}

/** Default datetime-local value (IST wall clock). */
export function defaultDatetimeLocal(date = new Date(), hm = '11:00'): string {
  const p = istParts(date);
  const time = hm.length >= 5 ? hm.slice(0, 5) : '11:00';
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${time}`;
}

/** Format HH:mm[:ss] for display (trim seconds when :00). */
export function formatTimeOfDay(time?: string | null): string {
  if (!time) return '—';
  const t = String(time).trim();
  if (/^\d{2}:\d{2}:\d{2}/.test(t)) return t.slice(0, 5);
  if (/^\d{2}:\d{2}/.test(t)) return t.slice(0, 5);
  return t;
}

/** Format HH:mm[:ss] as 12-hour with AM/PM (e.g. 15:30 → 3:30 PM). */
export function formatTime12h(time?: string | null): string {
  if (!time) return '—';
  const m = String(time).trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (!m) return String(time);
  let hour = Number(m[1]);
  const minute = m[2];
  if (Number.isNaN(hour) || hour < 0 || hour > 23) return String(time);
  const period = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12;
  if (hour === 0) hour = 12;
  return `${hour}:${minute} ${period}`;
}

/** Format YYYY-MM-DD as d MMM y in IST-safe way. */
export function formatDateOnly(isoDate?: string | null): string {
  if (!isoDate) return '—';
  const day = String(isoDate).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return String(isoDate);
  const d = new Date(`${day}T12:00:00${APP_TZ_OFFSET}`);
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: APP_TZ,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d);
}
